import BusinessIcon from "@mui/icons-material/Business";
import InfoIcon from "@mui/icons-material/Info";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Skeleton,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useAccount, useToast } from "../../../context";
import { useApiError } from "../../../hooks/useApiError";
import { Account, StatusColor } from "../../../interfaces/all";
import { getCompanyData, updateBusinessProfile } from "../../../services/api";
import { ErrorHandler } from "../../../utils/error-handler";
import { fallback } from "../../../utils/format";
import Header from "../../chat/Header/Header";

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ImageContainer = styled(Box)`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;

const StyledAvatar = styled(Avatar)`
  width: 100px;
  height: 100px;
  background-color: #e0f2f1;
  color: #008069;
  font-size: 50px;
  border: 3px solid #008069;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const BoxWrapper = styled(Box)`
  background: #ffffff;
  padding: 12px 20px;
  margin-bottom: 6px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled(Typography)`
  font-size: 13px;
  color: #008069;
  font-weight: 500;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoText = styled(Box)`
  color: #4a4a4a;
  font-size: 14px;
  margin: 3px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const InfoLabel = styled(Typography)`
  color: #4a4a4a;
  font-size: 14px;
`;

const StatusChip = styled(Box)<{ color: StatusColor }>`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => {
    switch (props.color) {
      case "success":
        return "#e0f2f1";
      case "warning":
        return "#fff3e0";
      case "error":
        return "#fbe9e7";
      default:
        return "#e3f2fd";
    }
  }};
  color: ${(props) => {
    switch (props.color) {
      case "success":
        return "#008069";
      case "warning":
        return "#f57c00";
      case "error":
        return "#d32f2f";
      default:
        return "#1976d2";
    }
  }};
`;

const getStatusColor = (status: string | undefined): StatusColor => {
  if (!status) return "info";

  const statusUpper = status.toUpperCase();

  // Status de verificação do código (code_verification_status)
  // Documentação: https://developers.facebook.com/docs/whatsapp/cloud-api/reference/whatsapp-business-api
  switch (statusUpper) {
    // Status de verificação
    case "VERIFIED":
      return "success"; // Conta verificada
    case "PENDING":
    case "IN_REVIEW":
      return "warning"; // Verificação em andamento
    case "REJECTED":
    case "EXPIRED":
      return "error"; // Verificação rejeitada ou expirada
    case "UNVERIFIED":
    case "NOT_VERIFIED":
      return "info"; // Conta não verificada

    // Status de qualidade
    case "GREEN":
      return "success"; // Alta qualidade
    case "YELLOW":
      return "warning"; // Qualidade média
    case "RED":
      return "error"; // Baixa qualidade
    case "UNKNOWN":
      return "info"; // Não avaliado

    // Status não mapeado
    default:
      return "info";
  }
};

const inputFieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: "#008069" },
    "&.Mui-focused fieldset": { borderColor: "#008069" },
  },
} as const;

/** Skeleton da página Perfil da Empresa (carregamento inicial e ao clicar em "Atualizar informações"). */
const CompanyProfileSkeleton = () => (
  <Container>
    <Box sx={{ height: "60px", flexShrink: 0 }}>
      <Header title="Perfil da Empresa" />
    </Box>
    <ContentContainer>
      <ImageContainer>
        <Skeleton variant="circular" width={100} height={100} />
      </ImageContainer>

      <BoxWrapper>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
          <Skeleton variant="circular" width={18} height={18} />
          <Skeleton variant="text" width="55%" height={16} />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.5,
          }}
        >
          <Skeleton variant="text" width="50%" height={20} />
          <Skeleton
            variant="rounded"
            width={72}
            height={22}
            sx={{ borderRadius: "10px" }}
          />
        </Box>
        <Skeleton variant="text" width="40%" height={18} />
      </BoxWrapper>

      <BoxWrapper>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
          <Skeleton variant="circular" width={18} height={18} />
          <Skeleton variant="text" width="15%" height={16} />
        </Box>
        <Skeleton variant="text" width="95%" height={18} sx={{ mb: 0.25 }} />
        <Skeleton variant="text" width="70%" height={18} />
      </BoxWrapper>

      <BoxWrapper>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
          <Skeleton variant="circular" width={18} height={18} />
          <Skeleton variant="text" width="30%" height={16} />
        </Box>
        <Skeleton variant="text" width="85%" height={18} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="60%" height={18} />
        <Skeleton variant="text" width="75%" height={18} sx={{ mt: 1 }} />
        <Skeleton
          variant="rounded"
          width="100%"
          height={40}
          sx={{ mt: 1.5, borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={40}
          sx={{ mt: 1, borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={56}
          sx={{ mt: 1, borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={40}
          sx={{ mt: 1, borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={40}
          sx={{ mt: 1, borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={40}
          sx={{ mt: 1, borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={40}
          sx={{ mt: 1, borderRadius: 1 }}
        />
        <Skeleton
          variant="rounded"
          width={140}
          height={36}
          sx={{ mt: 1.5, borderRadius: 1 }}
        />
      </BoxWrapper>
    </ContentContainer>
  </Container>
);

const CompanyProfile = () => {
  const { account, setAccount } = useAccount();
  const { showSuccess } = useToast();
  const { handleApiCall } = useApiError();
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [editAbout, setEditAbout] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editVertical, setEditVertical] = useState("");
  const [editWebsites, setEditWebsites] = useState<string[]>(["", ""]);
  const [error, setError] = useState<string | null>(null);

  // Simular loading inicial
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 800);
    return () => {
      clearTimeout(timer);
      setShowSkeleton(false);
    };
  }, []);

  // Sincronizar campos editáveis com o perfil da Meta quando account/whatsappProfile mudar
  useEffect(() => {
    const profile = account?.company?.meta?.whatsapp_profile;
    if (profile) {
      setEditAbout(profile.about ?? "");
      setEditAddress(profile.address ?? "");
      setEditDescription(profile.description ?? "");
      setEditEmail(profile.email ?? "");
      setEditVertical(profile.vertical ?? "");
      const sites = profile.websites ?? [];
      setEditWebsites([sites[0] ?? "", sites[1] ?? ""]);
    }
  }, [account?.company?.meta?.whatsapp_profile]);

  if (showSkeleton) {
    return <CompanyProfileSkeleton />;
  }

  if (!account || !account.company?.name || !account.company?.cnpj) {
    return (
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Perfil da Empresa" />
        </Box>
        <ContentContainer>
          <Box sx={{ p: 2 }}>
            <CompanyProfileSkeleton />
          </Box>
        </ContentContainer>
      </Container>
    );
  }

  const whatsappProfile = account.company.meta?.whatsapp_profile || {
    about: "",
    description: "",
    email: "",
    websites: [],
    vertical: "",
    profile_picture_url: "",
    messaging_product: "",
  };

  const whatsappBusinessInfo = account.company.meta?.whatsapp_business_info || {
    code_verification_status: "",
    quality_rating: "",
    platform_type: "",
    throughput: { level: "" },
    display_phone_number: "",
    verified_name: "",
    id: "",
  };

  const isAdministrator =
    account.role === "ADMINISTRATOR" ||
    account.role === "administrator" ||
    account.role?.toUpperCase() === "ADMINISTRATOR";

  const handleSaveProfile = async () => {
    const companyId = account.company.crm?.company_id || account.company._id;
    if (!companyId) return;
    setSavingProfile(true);
    await handleApiCall(
      async () => {
        const websites = editWebsites
          .map((u) => u.trim())
          .filter(Boolean)
          .slice(0, 2);
        await updateBusinessProfile(companyId, {
          about: editAbout.trim() || undefined,
          address: editAddress.trim() || undefined,
          description: editDescription.trim() || undefined,
          email: editEmail.trim() || undefined,
          vertical: editVertical.trim() || undefined,
          websites: websites.length ? websites : undefined,
        });
        const updated = await getCompanyData(account.company_id);
        if (updated.status === "success" && updated.data && setAccount) {
          const newCompany = updated.data as unknown as Account["company"];
          setAccount((prev) =>
            prev ? { ...prev, company: newCompany } : prev,
          );
        }
        return { status: "success" as const };
      },
      {
        onSuccess: () => {
          showSuccess("Perfil atualizado na Meta com sucesso.");
          setError(null);
        },
        onError: (err) => {
          const errorResponse = ErrorHandler.handleApiError(err);
          setError(
            errorResponse.userMessage ||
              errorResponse.message ||
              "Erro ao atualizar perfil na Meta",
          );
          setSavingProfile(false);
        },
        fallbackErrorMessage:
          "Erro ao atualizar perfil na Meta. Tente novamente.",
        successMessage: undefined,
      },
    );
    setSavingProfile(false);
  };

  const VERTICAL_OPTIONS = [
    { value: "", label: "—" },
    { value: "ALCOHOL", label: "Bebidas alcoólicas" },
    { value: "APPAREL", label: "Roupas e vestuário" },
    { value: "AUTO", label: "Automotivo" },
    { value: "BEAUTY", label: "Beleza, Spa e Salão" },
    { value: "EDU", label: "Educação" },
    { value: "ENTERTAIN", label: "Entretenimento" },
    { value: "EVENT_PLAN", label: "Eventos" },
    { value: "FINANCE", label: "Finanças e Bancos" },
    { value: "GOVT", label: "Serviço público" },
    { value: "GROCERY", label: "Alimentos e Mercado" },
    { value: "HEALTH", label: "Saúde" },
    { value: "HOTEL", label: "Hotéis e Hospedagem" },
    { value: "NONPROFIT", label: "Sem fins lucrativos" },
    { value: "ONLINE_GAMBLING", label: "Jogos e apostas online" },
    { value: "OTC_DRUGS", label: "Medicamentos sem prescrição" },
    { value: "PHYSICAL_GAMBLING", label: "Jogos e apostas (físico)" },
    { value: "PROF_SERVICES", label: "Serviços Profissionais" },
    { value: "RESTAURANT", label: "Restaurante" },
    { value: "RETAIL", label: "Varejo" },
    { value: "TRAVEL", label: "Viagens e Transporte" },
    { value: "OTHER", label: "Outro" },
  ];

  return (
    <Container>
      <Box sx={{ height: "60px", flexShrink: 0 }}>
        <Header title="Perfil da Empresa" />
      </Box>
      <ContentContainer>
        {error && (
          <Alert
            severity="error"
            sx={{ m: 2, borderRadius: "8px" }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        <ImageContainer>
          <StyledAvatar
            src={whatsappProfile?.profile_picture_url}
            alt={account.company.name}
          >
            <BusinessIcon sx={{ width: 50, height: 50 }} />
          </StyledAvatar>
        </ImageContainer>

        <BoxWrapper>
          <SectionTitle>
            <BusinessIcon fontSize="small" />
            Informações da Empresa
          </SectionTitle>
          <InfoText>
            <InfoLabel>{account.company.name}</InfoLabel>
            {whatsappBusinessInfo?.code_verification_status && (
              <StatusChip
                color={getStatusColor(
                  whatsappBusinessInfo.code_verification_status,
                )}
              >
                {whatsappBusinessInfo.code_verification_status}
              </StatusChip>
            )}
          </InfoText>
          <InfoText>
            <InfoLabel>CNPJ: {fallback(account.company.cnpj)}</InfoLabel>
          </InfoText>
        </BoxWrapper>

        <BoxWrapper>
          <SectionTitle>
            <InfoIcon fontSize="small" />
            Editar perfil na Meta
          </SectionTitle>
          <Typography
            variant="body2"
            sx={{ color: "#667781", fontSize: "12px", mb: 1.5 }}
          >
            Atualize os dados exibidos no perfil do WhatsApp.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ position: "relative" }}>
              <TextField
                label="Sobre"
                value={editAbout}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 139) setEditAbout(val);
                }}
                placeholder="Texto curto no perfil"
                size="small"
                fullWidth
                multiline
                rows={2}
                sx={inputFieldSx}
              />
              <Typography
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 12,
                  fontSize: "11px",
                  color: "#667781",
                  backgroundColor: "#fff",
                }}
              >
                {editAbout.length}/139
              </Typography>
            </Box>
            <Box sx={{ position: "relative" }}>
              <TextField
                label="Endereço"
                value={editAddress}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 256) setEditAddress(val);
                }}
                placeholder="Endereço do negócio"
                size="small"
                fullWidth
                multiline
                rows={2}
                sx={inputFieldSx}
              />
              <Typography
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 12,
                  fontSize: "11px",
                  color: "#667781",
                  backgroundColor: "#fff",
                }}
              >
                {editAddress.length}/256
              </Typography>
            </Box>
            <Box sx={{ position: "relative" }}>
              <TextField
                label="Descrição"
                value={editDescription}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 512) setEditDescription(val);
                }}
                placeholder="Descrição do negócio"
                size="small"
                fullWidth
                multiline
                rows={3}
                sx={inputFieldSx}
              />
              <Typography
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 12,
                  fontSize: "11px",
                  color: "#667781",
                  backgroundColor: "#fff",
                }}
              >
                {editDescription.length}/512
              </Typography>
            </Box>
            <Box sx={{ position: "relative" }}>
              <TextField
                label="E-mail"
                type="email"
                value={editEmail}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 128) setEditEmail(val);
                }}
                placeholder="E-mail de contato"
                size="small"
                fullWidth
                sx={inputFieldSx}
              />
              <Typography
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 12,
                  fontSize: "11px",
                  color: "#667781",
                  backgroundColor: "#fff",
                }}
              >
                {editEmail.length}/128
              </Typography>
            </Box>
            <TextField
              select
              label="Categoria (vertical)"
              value={editVertical}
              onChange={(e) => setEditVertical(e.target.value)}
              size="small"
              fullWidth
              SelectProps={{ native: true }}
              sx={inputFieldSx}
            >
              {VERTICAL_OPTIONS.map((opt: { value: string; label: string }) => (
                <option key={opt.value || "empty"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </TextField>
            <Box sx={{ position: "relative" }}>
              <TextField
                label="Site 1"
                value={editWebsites[0] ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 256) {
                    setEditWebsites((prev) => [val, prev[1] ?? ""]);
                  }
                }}
                placeholder="https://..."
                size="small"
                fullWidth
                sx={inputFieldSx}
              />
              <Typography
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 12,
                  fontSize: "11px",
                  color: "#667781",
                  backgroundColor: "#fff",
                }}
              >
                {(editWebsites[0] ?? "").length}/256
              </Typography>
            </Box>
            <Box sx={{ position: "relative" }}>
              <TextField
                label="Site 2"
                value={editWebsites[1] ?? ""}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 256) {
                    setEditWebsites((prev) => [prev[0] ?? "", val]);
                  }
                }}
                placeholder="https://..."
                size="small"
                fullWidth
                sx={inputFieldSx}
              />
              <Typography
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 12,
                  fontSize: "11px",
                  color: "#667781",
                  backgroundColor: "#fff",
                }}
              >
                {(editWebsites[1] ?? "").length}/256
              </Typography>
            </Box>
          </Box>
        </BoxWrapper>
      </ContentContainer>

      {/* Botão fixo no rodapé - apenas para ADMINISTRATOR */}
      {isAdministrator && (
        <Box
          sx={{
            p: 2,
            textAlign: "center",
            borderTop: "1px solid #f0f0f0",
            backgroundColor: "#fff",
            position: "sticky",
            bottom: 0,
            zIndex: 11,
            minHeight: "64px",
            maxHeight: "64px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            size="small"
            disabled={savingProfile}
            startIcon={savingProfile ? null : <SaveIcon />}
            onClick={handleSaveProfile}
            fullWidth
            sx={{
              backgroundColor: "#008069",
              "&:hover": {
                backgroundColor: "#006d5a",
              },
              "&:disabled": {
                backgroundColor: "#cccccc",
              },
              fontSize: "14px",
              padding: "8px",
              textTransform: "none",
              fontWeight: 500,
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "14px" }}>
              {savingProfile ? "Salvando…" : "Salvar perfil na Meta"}
            </Typography>
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default CompanyProfile;
